import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CirclePlus, Pencil, Play, Plus, Square, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast';
import { billingApi } from '@/lib/api/endpoints';
import type { TaskDetail } from '@/lib/api/types';
import { describeFailure } from '@/lib/i18n/errors';
import { useT } from '@/lib/i18n';
import { formatMinutes } from '@/routes/Clients';

/**
 * Tracked time on one task: the clock, the history, and the forgotten hour
 * typed in afterwards.
 *
 * Shown only when the task can bill somewhere (it has a client, own or
 * inherited) — or already has entries, so history never hides. A timer on a
 * task with no client would produce minutes no report can ever pick up.
 */
export function TimeSection({ task }: { task: TaskDetail }) {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data: entries } = useQuery({
    queryKey: ['time-entries', task.id],
    queryFn: () => billingApi.taskEntries(task.id),
  });
  const { data: timer } = useQuery({
    queryKey: ['timer'],
    queryFn: () => billingApi.timer(),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['timer'] });
    void queryClient.invalidateQueries({ queryKey: ['time-entries', task.id] });
  };
  const start = useMutation({
    mutationFn: (parallel: boolean) => billingApi.startTimer(task.id, parallel),
    onSuccess: refresh,
    onError: (failure) => toast.error(describeFailure(failure, 'common.error')),
  });
  const stop = useMutation({
    mutationFn: () => billingApi.stopTimer(task.id),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => billingApi.deleteEntry(id),
    onSuccess: refresh,
    onError: (failure) => toast.error(describeFailure(failure, 'common.error')),
  });

  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const update = useMutation({
    mutationFn: (input: { id: string; started_at: string; ended_at: string }) =>
      billingApi.updateEntry(input.id, {
        started_at: input.started_at,
        ended_at: input.ended_at,
      }),
    onSuccess: () => {
      setEditing(null);
      refresh();
    },
    onError: (failure) => toast.error(describeFailure(failure, 'common.error')),
  });
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const add = useMutation({
    mutationFn: () =>
      billingApi.addEntry({
        task_id: task.id,
        started_at: new Date(startAt).toISOString(),
        ended_at: new Date(endAt).toISOString(),
      }),
    onSuccess: () => {
      setAdding(false);
      setStartAt('');
      setEndAt('');
      refresh();
    },
    onError: (failure) => toast.error(describeFailure(failure, 'common.error')),
  });

  const hasEntries = (entries?.length ?? 0) > 0;
  if (!task.client_id && !hasEntries) return null;

  const running = timer?.entries ?? [];
  const runningHere = running.some((item) => item.entry.task_id === task.id);
  const runningElsewhere = running.length > 0 && !runningHere;
  const total = (entries ?? []).reduce((sum, entry) => sum + entry.minutes, 0);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="flex items-baseline gap-2 text-xs text-content-subtle">
          {t('time.tracked')}
          {total > 0 ? (
            <span className="font-medium tabular-nums text-content">
              {formatMinutes(total)}
            </span>
          ) : null}
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAdding((open) => !open)}
            title={t('time.addManual')}
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
          </Button>
          {runningHere ? (
            <Button variant="secondary" size="sm" onClick={() => stop.mutate()}>
              <Square className="mr-1.5 h-3 w-3 fill-current" aria-hidden />
              {t('time.stop')}
            </Button>
          ) : (
            <>
              {runningElsewhere ? (
                <Button
                  variant="ghost"
                  size="sm"
                  title={t('time.startParallel')}
                  onClick={() => start.mutate(true)}
                >
                  <CirclePlus className="h-3.5 w-3.5" aria-hidden />
                </Button>
              ) : null}
              <Button variant="secondary" size="sm" onClick={() => start.mutate(false)}>
                <Play className="mr-1.5 h-3 w-3" aria-hidden />
                {t('time.start')}
              </Button>
            </>
          )}
        </div>
      </div>

      {adding ? (
        <form
          className="mb-2 flex flex-wrap items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (startAt && endAt) add.mutate();
          }}
        >
          <input
            type="datetime-local"
            value={startAt}
            onChange={(event) => setStartAt(event.target.value)}
            className="rounded-lg bg-surface px-2 py-1.5 text-xs ring-1 ring-border text-content placeholder:text-content-subtle"
          />
          <span className="text-xs text-content-subtle">–</span>
          <input
            type="datetime-local"
            value={endAt}
            onChange={(event) => setEndAt(event.target.value)}
            className="rounded-lg bg-surface px-2 py-1.5 text-xs ring-1 ring-border text-content placeholder:text-content-subtle"
          />
          <Button type="submit" size="sm" disabled={!startAt || !endAt || add.isPending}>
            {t('time.save')}
          </Button>
        </form>
      ) : null}

      {hasEntries ? (
        <ul className="divide-y divide-border rounded-lg bg-surface ring-1 ring-border text-content placeholder:text-content-subtle">
          {(entries ?? []).slice(0, 8).map((entry) =>
            editing === entry.id ? (
              <EntryEditor
                key={entry.id}
                entry={entry}
                pending={update.isPending}
                onCancel={() => setEditing(null)}
                onSave={(started, ended) =>
                  update.mutate({ id: entry.id, started_at: started, ended_at: ended })
                }
              />
            ) : (
              <li key={entry.id} className="group flex items-center gap-2 px-3 py-1.5 text-xs">
                <span className="text-content-subtle">
                  {formatEntryDay(entry.started_at)}
                </span>
                <span className="flex-1" />
                {entry.work_report_id ? (
                  <span className="text-content-subtle">{t('time.billed')}</span>
                ) : null}
                <span className="tabular-nums">
                  {entry.ended_at ? formatMinutes(entry.minutes) : t('time.running')}
                </span>
                {!entry.work_report_id && entry.ended_at ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setEditing(entry.id)}
                      className="rounded p-0.5 text-content-subtle opacity-0 transition-surface hover:text-content group-hover:opacity-100"
                      aria-label={t('time.edit')}
                    >
                      <Pencil className="h-3 w-3" aria-hidden />
                    </button>
                    <button
                      type="button"
                      onClick={() => remove.mutate(entry.id)}
                      className="rounded p-0.5 text-content-subtle opacity-0 transition-surface hover:text-danger group-hover:opacity-100"
                      aria-label={t('time.delete')}
                    >
                      <Trash2 className="h-3 w-3" aria-hidden />
                    </button>
                  </>
                ) : null}
              </li>
            ),
          )}
        </ul>
      ) : null}
    </div>
  );
}

/** One entry opened for correction — the times, editable in place. */
function EntryEditor({
  entry,
  pending,
  onSave,
  onCancel,
}: {
  entry: { id: string; started_at: string; ended_at: string | null };
  pending: boolean;
  onSave: (startedIso: string, endedIso: string) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const [start, setStart] = useState(toLocalInput(entry.started_at));
  const [end, setEnd] = useState(entry.ended_at ? toLocalInput(entry.ended_at) : '');

  return (
    <li className="flex flex-wrap items-center gap-2 px-3 py-1.5">
      <input
        type="datetime-local"
        value={start}
        onChange={(event) => setStart(event.target.value)}
        className="rounded bg-canvas px-1.5 py-1 text-xs ring-1 ring-border text-content placeholder:text-content-subtle"
      />
      <span className="text-xs text-content-subtle">–</span>
      <input
        type="datetime-local"
        value={end}
        onChange={(event) => setEnd(event.target.value)}
        className="rounded bg-canvas px-1.5 py-1 text-xs ring-1 ring-border text-content placeholder:text-content-subtle"
      />
      <Button
        size="sm"
        disabled={!start || !end || pending}
        onClick={() =>
          onSave(new Date(start).toISOString(), new Date(end).toISOString())
        }
      >
        {t('time.save')}
      </Button>
      <Button size="sm" variant="ghost" onClick={onCancel}>
        {t('common.cancel')}
      </Button>
    </li>
  );
}

/** ISO timestamp → the local-time value a datetime-local input expects. */
function toLocalInput(iso: string): string {
  const date = new Date(iso);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

function formatEntryDay(iso: string): string {
  const date = new Date(iso);
  return `${date.getDate()}. ${date.getMonth() + 1}. ${String(date.getHours()).padStart(2, '0')}:${String(
    date.getMinutes(),
  ).padStart(2, '0')}`;
}
